import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { SafeAreas } from "../../safe_areas.js";
import { Game } from "../../../game.js";
import { Vector2 } from "../../../common/vector2.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { GameSettingsMenu } from "../game_screen/ui/game_settings_menu.js";
import { RewardWidgetsService } from "../../../reward_widgets/reward_widgets_service.js";
import { adoptEconomyFromPlayer } from "../../../api/player_sync.js";
import type { PlayerDto } from "../../../api/dto/player.dto.js";
import type { HappyFallStateDto } from "../../../api/dto/reward_widgets.dto.js";
import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";
import { StatisticsStorage, type GameStatistics } from "../../../storage/statistics_storage.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import type { HappyFallResultOverlayParams } from "../../overlay/overlays/happy_fall_result_overlay.js";
import { RewardFlyToHud, type RewardFlySource } from "../../reward_fly/reward_fly_to_hud.js";
import type { ContinueOverlayParams } from "../../overlay/overlays/continue_overlay.js";
import { HappyFall3D, HappyFallSceneFrame } from "./happy_fall_3d.js";
import { PlinkoBoard } from "./plinko_board.js";
import { PlinkoGame } from "./plinko_game.js";
import { PlinkoLayout } from "./plinko_layout.js";
import { PlinkoPhysics } from "./plinko_physics.js";

export class HappyFallScreen extends UIElement
{
  private static readonly BG_W = 941;
  private static readonly BG_H = 1672;
  private static readonly BG_PEDESTAL_Y = 1500;
  private static readonly DESIGN_W = 1080;
  private static readonly DESIGN_H = 1920;
  private static readonly BG_PEDESTAL_ABOVE_BOTTOM = -70;
  private static readonly CLAIM_HUD_HOLD_MS = 200;
  private static readonly BG_SCALE = 1.35;
  private static readonly HEADER_H = 312;
  private static readonly BOARD_X = ( HappyFallScreen.DESIGN_W - PlinkoLayout.BOARD_W ) / 2;
  private static readonly BLOCK_H = HappyFallScreen.HEADER_H + PlinkoLayout.BOARD_H;
  private static readonly CANNON_ZONE_H = HappyFallScreen.DESIGN_H - HappyFallScreen.BLOCK_H;
  private static readonly CANNON_BELOW_BOTTOM = 160;

  private static readonly HEADER_SIDE_INSET = 40;
  private static readonly BALLS_W = 187;
  private static readonly BALLS_H = 215;
  private static readonly BALLS_Y = 100;
  private static readonly LOGO_W = 600;
  private static readonly LOGO_H = 279;
  private static readonly LOGO_Y = 130;
  private static readonly SETTINGS_SIZE = 100;
  private static readonly SETTINGS_Y = 100;

  private static readonly INTRO_HEADER_H = 450;
  private static readonly INTRO_LOGO_W = 580;
  private static readonly INTRO_LOGO_H = 269;
  private static readonly INTRO_LOGO_Y = 150;
  private static readonly INTRO_CLOSE_SIZE = 132;
  private static readonly INTRO_CLOSE_RIGHT = 59;
  private static readonly INTRO_CLOSE_Y = 226;
  private static readonly INTRO_RACCOON_SIZE = 620;
  private static readonly INTRO_RACCOON_SHIFT_Y = 48;
  private static readonly INTRO_BUTTON_W = 630;
  private static readonly INTRO_BUTTON_H = 252;
  private static readonly INTRO_BUTTON_BOTTOM = 240;
  private static readonly INTRO_BUTTON_TEXT_INSET = 60;
  private static readonly INTRO_BUTTON_FONT = 90;
  private static readonly INTRO_LOADING_FONT = 80;
  private static readonly INTRO_FADE_MS = 240;
  private static readonly INTRO_POP_UP_MS = 176;
  private static readonly INTRO_POP_SET_MS = 112;
  private static readonly INTRO_POP_SCALE = 1.15;

  private static readonly PRELOAD_IMAGES: ImgID[] = [
    ImgID.UI_HappyFallBg,
    ImgID.UI_HappyFallBoard,
    ImgID.UI_HappyFallHole,
    ImgID.UI_HappyFallLogo,
    ImgID.UI_GameBlockBg,
    ImgID.IconSettings,
    ImgID.IconCoin,
    ImgID.UI_PlayButton,
    ImgID.UI_ConveyorResultBanner,
    ImgID.UI_ShopBlockImg,
    ImgID.UI_SettingsDialogCloseBtn,
    ImgID.UI_BlueBtn,
    ImgID.UI_GreenFrameBtn,
    ImgID.UI_GrayBtn,
    ImgID.UI_PaternedBg,
  ];

  public static active: HappyFallScreen | null = null;

  private readonly body: UIElementHolder;
  private readonly body_w: number;
  private readonly body_x: number;
  private readonly scene_frame: HappyFallSceneFrame;

  private events_controller: BasicEventsController | null = null;
  private board!: PlinkoBoard;
  private game!: PlinkoGame;
  private balls_text!: UITextElement;
  private settings_button!: UIImage;
  private settings_menu!: GameSettingsMenu;
  private intro!: UIElementHolder;
  private intro_body!: UIElementHolder;
  private intro_header!: UIElementHolder;
  private intro_loading_text!: UITextElement;
  private view: HappyFall3D | null = null;
  private balls_left = 0;
  private pending_drops = 0;
  private drop_seq = 0;
  private adopted_seq = 0;
  private latest_player: PlayerDto | null = null;
  private exiting = false;
  private intro_open = true;
  private hole_pressed = false;
  private round_shots = 0;
  private round_rewards: DailyTaskRewardDto[] = [];
  private round_stats: GameStatistics | null = null;
  private round_result_open = false;

  public constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.body_w = LayoutScaler.widthFromDesign( HappyFallScreen.DESIGN_W );
    this.body_x = ( width - this.body_w ) / 2;

    const scale = this.body_w / HappyFallScreen.DESIGN_W;
    const content_h = height / scale;
    const block_top = Math.max( 0, ( content_h - HappyFallScreen.CANNON_ZONE_H - HappyFallScreen.BLOCK_H ) / 2 );
    const board_top = block_top + HappyFallScreen.HEADER_H;
    const body_y = block_top * scale;
    const body_h = HappyFallScreen.BLOCK_H * scale;

    this.scene_frame = {
      x: -HappyFallScreen.BOARD_X,
      y: -board_top,
      w: HappyFallScreen.DESIGN_W,
      h: content_h + Game.safeAreaBottom() / scale,
      cannon_y: content_h - board_top + HappyFallScreen.CANNON_BELOW_BOTTOM,
    };

    this.body = new UIElementHolder( ctx, this.body_x, body_y, this.body_w, body_h );

    HappyFallScreen.active = this;

    this.initializeEventsController( ctx.canvas );
    this.createElements();
    void this.loadResources();
  }

  private async loadResources(): Promise<void>
  {
    const images = HappyFallScreen.PRELOAD_IMAGES.map( id => ImagesHolder.ensure( id ).catch( () => undefined ) );
    await Promise.all( [ ResourceManager.whenReady(), RewardWidgetsService.refresh(), ...images ] );
    if ( this.exiting )
      return;

    const state = RewardWidgetsService.happyFall();
    if ( !state )
    {
      this.intro_loading_text.setLocalizationKey( "LOADING_ERROR.TITLE" );
      this.createIntroCloseButton();
      this.invalidateSubtree();
      return;
    }

    const refill_at = RewardWidgetsService.happyFallRefillAt();
    if ( refill_at !== null )
    {
      this.exitToMenu();
      OverlayFactory.openOverlay( OverlayId.HAPPY_FALL_WAIT_OVERLAY, { refill_at } );
      return;
    }

    const icons =state.slots.flatMap( reward => reward ? [ ImagesHolder.ensure( PlinkoBoard.rewardIcon( reward ) ).catch( () => undefined ) ] : [] );
    await Promise.all( icons );
    if ( this.exiting )
      return;

    this.board.setSlotRewards( state.slots );
    this.setBallsLeft( state.balls_total - state.balls_used + state.bonus_balls );

    const scene_h = this.scene_frame.h * this.body_w / HappyFallScreen.DESIGN_W;
    this.view = new HappyFall3D( this.body_w, scene_h, Game.getDpr(), this.scene_frame );
    this.view.warmUp();
    this.game.setView( this.view );
    this.invalidateSubtree();
    this.intro_body.removeChild( this.intro_loading_text );
    this.createIntroCloseButton();
    this.createIntroPlayButton();
  }

  private createElements(): void
  {
    this.createBackground();
    this.addChild( this.body );

    const board_x = LayoutScaler.widthFromDesign( HappyFallScreen.BOARD_X );
    const board_y = LayoutScaler.widthFromDesign( HappyFallScreen.HEADER_H );
    const board_w = LayoutScaler.widthFromDesign( PlinkoLayout.BOARD_W );
    const board_h = LayoutScaler.widthFromDesign( PlinkoLayout.BOARD_H );

    this.board = new PlinkoBoard( this.ctx, board_x, board_y, board_w, board_h );
    this.body.addChild( this.board );

    this.createHeader();

    this.game = new PlinkoGame( this.ctx, board_x, board_y, board_w, board_h, this.scene_frame );
    this.body.addChild( this.game );

    this.createSettingsMenu();
    this.createIntro();
  }

  private createBackground(): void
  {
    const screen_left = -Game.safeAreaLeft();
    const screen_top = -Game.safeAreaTop();
    const screen_w = Game.screenW();
    const screen_h = Game.screenH();
    const screen_bottom = screen_top + screen_h;

    const pedestal_y = this.rect.getH() - LayoutScaler.widthFromDesign( HappyFallScreen.BG_PEDESTAL_ABOVE_BOTTOM );
    const cover_top = ( pedestal_y - screen_top ) / HappyFallScreen.BG_PEDESTAL_Y;
    const cover_bottom = ( screen_bottom - pedestal_y ) / ( HappyFallScreen.BG_H - HappyFallScreen.BG_PEDESTAL_Y );
    const cover = Math.max( screen_w / HappyFallScreen.BG_W * HappyFallScreen.BG_SCALE, cover_top, cover_bottom );
    const bg_w = HappyFallScreen.BG_W * cover;
    const bg_h = HappyFallScreen.BG_H * cover;
    const bg_x = screen_left + ( screen_w - bg_w ) / 2;
    const bg_y = pedestal_y - HappyFallScreen.BG_PEDESTAL_Y * cover;

    const bg = new UIImage( this.ctx, bg_x, bg_y, bg_w, bg_h, ImgID.UI_HappyFallBg );
    bg.setClickable( false );
    this.addChild( bg );
  }

  private createHeader(): void
  {
    const header_h = LayoutScaler.widthFromDesign( HappyFallScreen.HEADER_H );

    const header = new UIElementHolder( this.ctx, 0, 0, this.body_w, header_h );
    this.body.addChild( header );

    this.createLogo( header );
    this.createBallsBlock( header );
    this.createSettingsButton( header );
  }

  private createLogo( header: UIElementHolder ): void
  {
    const w = LayoutScaler.widthFromDesign( HappyFallScreen.LOGO_W );
    const h = LayoutScaler.widthFromDesign( HappyFallScreen.LOGO_H );
    const x = ( header.getRect().getW() - w ) / 2;
    const y = LayoutScaler.widthFromDesign( HappyFallScreen.LOGO_Y );

    const logo = new UIImage( this.ctx, x, y, w, h, ImgID.UI_HappyFallLogo );
    logo.setClickable( false );
    header.addChild( logo );
  }

  private createBallsBlock( header: UIElementHolder ): void
  {
    const bg_w = LayoutScaler.widthFromDesign( HappyFallScreen.BALLS_W );
    const bg_h = LayoutScaler.widthFromDesign( HappyFallScreen.BALLS_H );
    const bg_x = LayoutScaler.widthFromDesign( HappyFallScreen.HEADER_SIDE_INSET );
    const bg_y = LayoutScaler.widthFromDesign( HappyFallScreen.BALLS_Y );

    const bg = new UIImage( this.ctx, bg_x, bg_y, bg_w, bg_h, ImgID.UI_GameBlockBg );
    bg.setClickable( false );
    header.addChild( bg );

    const title_w = LayoutScaler.widthFromDesign( 200 );
    const title_h = LayoutScaler.widthFromDesign( 54 );
    const title_x = ( bg_w - title_w ) / 2;
    const title_y = LayoutScaler.widthFromDesign( 13 );

    const title = new UITextElement( this.ctx, title_x, title_y, title_w, title_h, {
      localization_key: "BALLS",
      font_size: LayoutScaler.textSizeFromDesign( 45 ),
      font_weight: 700,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      vertical_align: MyTextVerticalAlign.Center,
      text_align: MyTextAlign.Center,
      shadow_offset_x: 0,
      shadow_offset_y: 0
    } );
    bg.addChild( title );

    const count_w = LayoutScaler.widthFromDesign( 200 );
    const count_h = LayoutScaler.widthFromDesign( 109 );
    const count_x = ( bg_w - count_w ) / 2;
    const count_y = LayoutScaler.widthFromDesign( 85 );

    this.balls_text = new UITextElement( this.ctx, count_x, count_y, count_w, count_h, {
      text: `${this.balls_left}`,
      font_size: LayoutScaler.textSizeFromDesign( 78 ),
      font_weight: 700,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      vertical_align: MyTextVerticalAlign.Center,
      text_align: MyTextAlign.Center,
      shadow_offset_x: 0,
      shadow_offset_y: 0
    } );
    bg.addChild( this.balls_text );
  }

  private createSettingsButton( header: UIElementHolder ): void
  {
    const size = LayoutScaler.widthFromDesign( HappyFallScreen.SETTINGS_SIZE );
    const x = header.getRect().getW() - LayoutScaler.widthFromDesign( HappyFallScreen.HEADER_SIDE_INSET ) - size;
    const y = LayoutScaler.widthFromDesign( HappyFallScreen.SETTINGS_Y );

    this.settings_button = new UIImage( this.ctx, x, y, size, size, ImgID.IconSettings, {}, true );
    this.settings_button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.settings_menu.toggle();
    } );
    header.addChild( this.settings_button );
  }

  private createSettingsMenu(): void
  {
    const button_pos = this.settings_button.getAbsolutePosition();
    const screen_pos = this.getAbsolutePosition();
    const x = button_pos.x - screen_pos.x;
    const y = button_pos.y - screen_pos.y;
    const w = this.settings_button.getRect().getW();
    const h = this.settings_button.getRect().getH();

    this.settings_menu = new GameSettingsMenu(
      this.ctx, this.rect.getW(), this.rect.getH(), x, y, w, h,
      () => this.invalidateSubtree(),
      () => this.exitToMenu(),
      () => false,
      () => {},
      () => false
    );
    this.addChild( this.settings_menu );
  }

  private createIntro(): void
  {
    this.intro = new UIElementHolder( this.ctx, 0, 0, this.rect.getW(), this.rect.getH() );
    this.addChild( this.intro );

    this.createIntroBackground();

    const body_h = LayoutScaler.widthFromDesign( HappyFallScreen.DESIGN_H );
    const body_y = ( this.rect.getH() - body_h ) / 2;

    this.intro_body = new UIElementHolder( this.ctx, this.body_x, body_y, this.body_w, body_h );
    this.intro.addChild( this.intro_body );

    this.createIntroHeader();
    this.createIntroRaccoon();
    this.createIntroLoadingText();
  }

  private createIntroBackground(): void
  {
    const x = -Game.safeAreaLeft();
    const y = -Game.safeAreaTop();
    const w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const h = this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom();

    const bg = new RoundedRectangle( this.ctx, x, y, w, h, 0, ColorScheme.happy_fall_intro_bg );
    bg.setClickable( false );
    this.intro.addChild( bg );
  }

  private createIntroHeader(): void
  {
    const header_h = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_HEADER_H );

    this.intro_header = new UIElementHolder( this.ctx, 0, 0, this.body_w, header_h );
    this.intro_body.addChild( this.intro_header );

    const logo_w = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_LOGO_W );
    const logo_h = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_LOGO_H );
    const logo_x = ( this.body_w - logo_w ) / 2;
    const logo_y = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_LOGO_Y );

    const logo = new UIImage( this.ctx, logo_x, logo_y, logo_w, logo_h, ImgID.UI_HappyFallLogo );
    logo.setClickable( false );
    this.intro_header.addChild( logo );
  }

  private createIntroCloseButton(): void
  {
    const close_size = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_CLOSE_SIZE );
    const close_x = this.body_w - LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_CLOSE_RIGHT ) - close_size;
    const close_y = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_CLOSE_Y );

    const close = new UIImage( this.ctx, close_x, close_y, close_size, close_size, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    close.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.exitToMenu();
    } );
    this.intro_header.addChild( close );
    this.popIn( close );
  }

  private createIntroRaccoon(): void
  {
    const size = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_RACCOON_SIZE );
    const x = ( this.intro_body.getRect().getW() - size ) / 2;
    const y = ( this.intro_body.getRect().getH() - size ) / 2 - LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_RACCOON_SHIFT_Y );

    const raccoon = new UIImage( this.ctx, x, y, size, size, ImgID.UI_HappyFallRaccoon );
    raccoon.setClickable( false );
    this.intro_body.addChild( raccoon );
  }

  private createIntroLoadingText(): void
  {
    const w = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_BUTTON_W );
    const h = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_BUTTON_H );
    const x = ( this.intro_body.getRect().getW() - w ) / 2;
    const y = this.intro_body.getRect().getH() - LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_BUTTON_BOTTOM ) - h;

    this.intro_loading_text = new UITextElement( this.ctx, x, y, w, h, {
      localization_key: "LOADING",
      font_size: LayoutScaler.textSizeFromDesign( HappyFallScreen.INTRO_LOADING_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.booster_dialog_quantity_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      shadow_offset_x: 0,
      shadow_offset_y: 0,
      auto_wrap: false,
      fit_font_size: true,
    } );
    this.intro_loading_text.setClickable( false );
    this.intro_body.addChild( this.intro_loading_text );
  }

  private createIntroPlayButton(): void
  {
    const w = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_BUTTON_W );
    const h = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_BUTTON_H );
    const x = ( this.intro_body.getRect().getW() - w ) / 2;
    const y = this.intro_body.getRect().getH() - LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_BUTTON_BOTTOM ) - h;

    const button = new UIImage( this.ctx, x, y, w, h, ImgID.UI_PlayButton, {}, true );
    button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.closeIntro();
    } );
    this.intro_body.addChild( button );

    const text_inset = LayoutScaler.widthFromDesign( HappyFallScreen.INTRO_BUTTON_TEXT_INSET );
    const text_w = w - text_inset * 2;

    const text = new UITextElement( this.ctx, text_inset, 0, text_w, h, {
      localization_key: "PLAY",
      font_size: LayoutScaler.textSizeFromDesign( HappyFallScreen.INTRO_BUTTON_FONT ),
      font_weight: 800,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.menu_button_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.black_transparent,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      scale_to_width: true,
      auto_wrap: false,
    } );
    button.addChild( text, true );
    this.popIn( button );
  }

  private popIn( element: UIImage ): void
  {
    const start = performance.now();
    const zero = new Vector2( 0, 0 );
    const peak = new Vector2( HappyFallScreen.INTRO_POP_SCALE, HappyFallScreen.INTRO_POP_SCALE );
    const rest = new Vector2( 1, 1 );

    element.setScale( zero );
    element.addTransform( new TransformScale( element, true, zero, peak, start, HappyFallScreen.INTRO_POP_UP_MS, { easing: Easing.EaseOutQuad } ) );
    element.addTransform( new TransformScale( element, true, peak, rest, start + HappyFallScreen.INTRO_POP_UP_MS, HappyFallScreen.INTRO_POP_SET_MS, { easing: Easing.EaseOutQuad } ) );
  }

  private closeIntro(): void
  {
    this.intro_open = false;
    this.intro.setClickable( false );
    this.intro.hideElement( HappyFallScreen.INTRO_FADE_MS );
  }

  private toGamePoint( p: Vector2 ): { x: number; y: number }
  {
    const rect = this.game.getAbsoluteRect();
    return this.game.toDesign( p.x - rect.x, p.y - rect.y );
  }

  private isHolePoint( p: Vector2 ): boolean
  {
    const point = this.toGamePoint( p );
    return PlinkoLayout.isInHole( point.x, point.y );
  }

  private onPointerStart( p: Vector2 ): void
  {
    this.hole_pressed = !this.intro_open && this.balls_left > 0 && !this.settings_menu.isBusy() && this.isHolePoint( p );

    if ( !this.hole_pressed )
      super.onTouchStart( p, performance.now() );
  }

  private onPointerMove( p: Vector2 ): void
  {
    if ( !this.hole_pressed )
      super.onTouchMove( p, performance.now() );
  }

  private onPointerEnd( p: Vector2 ): void
  {
    if ( !this.hole_pressed )
    {
      super.onTouchEnd( p, performance.now() );
      return;
    }

    this.hole_pressed = false;
    if ( !this.isHolePoint( p ) )
      return;

    const aim_x = this.toGamePoint( p ).x;
    if ( this.round_shots === 0 )
      this.round_stats = StatisticsStorage.load();
    this.round_shots++;
    this.setBallsLeft( this.balls_left - 1 );
    this.game.aim( aim_x );
    void this.drop( aim_x );
  }

  public syncBalls( state: HappyFallStateDto ): void
  {
    this.setBallsLeft( state.balls_total - state.balls_used + state.bonus_balls - this.pending_drops );
  }

  private setBallsLeft( balls: number ): void
  {
    this.balls_left = Math.max( 0, balls );
    this.balls_text.setText( `${this.balls_left}` );
  }

  private async drop( aim_x: number ): Promise<void>
  {
    this.pending_drops++;

    try
    {
      const result = await RewardWidgetsService.dropHappyFall();
      const seq = ++this.drop_seq;
      this.pending_drops--;
      this.latest_player = result.player;

      if ( this.exiting )
      {
        this.adoptPlayer( seq, result.player );
        return;
      }

      this.setBallsLeft( result.balls_total - result.balls_used + result.bonus_balls - this.pending_drops );

      const reward = result.reward;
      const plan = PlinkoPhysics.findDrop( aim_x, this.dropSlots( result.slot_index ) );
      const label = reward ? PlinkoBoard.floatLabel( reward ) : null;
      this.game.shoot( plan, label, () =>
      {
        this.adoptPlayer( seq, result.player );
        if ( reward )
          this.round_rewards.push( reward );
      }, performance.now() );
    }
    catch ( e )
    {
      console.warn( "[HappyFall] drop failed", e );
      this.pending_drops--;
      this.round_shots--;
      this.setBallsLeft( this.balls_left + 1 );
    }
  }

  private isRoundOver(): boolean
  {
    return !this.round_result_open && !this.exiting && this.round_shots > 0 && this.balls_left === 0
      && this.pending_drops === 0 && !this.game.hasActiveBalls();
  }

  private roundRewards(): DailyTaskRewardDto[]
  {
    const totals = new Map<DailyTaskRewardDto[ "type" ], number>();
    for ( const reward of this.round_rewards )
      totals.set( reward.type, ( totals.get( reward.type ) ?? 0 ) + reward.amount );
    return [ ...totals ].map( ( [ type, amount ] ) => ( { type, amount } ) );
  }

  private showRoundResult(): void
  {
    this.round_result_open = true;

    const stats_before = this.round_stats ?? StatisticsStorage.load();
    const params: HappyFallResultOverlayParams = {
      rewards: this.roundRewards(),
      onClaim: sources => this.claimRoundRewards( sources, stats_before ),
    };

    OverlayFactory.openOverlay( OverlayId.HAPPY_FALL_RESULT_OVERLAY, params );
  }

  private claimRoundRewards( sources: RewardFlySource[], stats_before: GameStatistics ): void
  {
    const now = performance.now();
    const end = RewardFlyToHud.play( this.ctx, this, sources, stats_before, HappyFallScreen.CLAIM_HUD_HOLD_MS );
    RewardFlyToHud.schedule( this.ctx, this, now, end, () => this.openBuyBalls() );
  }

  private openBuyBalls(): void
  {
    const state = RewardWidgetsService.happyFall();
    if ( !state || this.exiting )
    {
      this.exitToMenu();
      return;
    }

    const params: ContinueOverlayParams = {
      offer: { price: state.buy_price, shots: state.buy_balls },
      purchase_index: 0,
      buy: () => this.buyBalls(),
      onBuy: () => this.startRound(),
      onClose: () => this.exitToMenu(),
    };

    OverlayFactory.openOverlay( OverlayId.CONTINUE_OVERLAY, params );
  }

  private async buyBalls(): Promise<boolean>
  {
    try
    {
      const result = await RewardWidgetsService.buyHappyFallBalls();
      if ( result.player )
        adoptEconomyFromPlayer( result.player );
      this.syncBalls( result.happy_fall );
      return true;
    }
    catch ( e )
    {
      console.warn( "[HappyFall] buy balls failed", e );
      return false;
    }
  }

  private startRound(): void
  {
    this.round_shots = 0;
    this.round_rewards = [];
    this.round_stats = null;
    this.round_result_open = false;
    this.game.clearLanded();
  }

  private dropSlots( slot_index: number ): number[]
  {
    const mirror = PlinkoLayout.DIVIDER_COUNT - slot_index;
    return mirror === slot_index ? [ slot_index ] : [ slot_index, mirror ];
  }

  private adoptPlayer( seq: number, player: PlayerDto | null ): void
  {
    if ( !player || seq <= this.adopted_seq )
      return;

    this.adopted_seq = seq;
    adoptEconomyFromPlayer( player );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => this.onPointerStart( p ),
      ( p: Vector2 ) => this.onPointerEnd( p ),
      ( p: Vector2 ) => this.onPointerMove( p ),
      true
    );
  }

  private exitToMenu(): void
  {
    if ( this.exiting )
      return;

    this.exiting = true;
    void ScreenManager.showScreen( Screens.MENU );
  }

  public destroy(): void
  {
    if ( HappyFallScreen.active === this )
      HappyFallScreen.active = null;

    this.exiting = true;
    this.adoptPlayer( this.drop_seq, this.latest_player );
    this.view?.dispose();
    this.view = null;

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }

  public update( now: number ): void
  {
    this.game.update( now );
    if ( this.isRoundOver() )
      this.showRoundResult();
    this.settings_menu.tick( now );
    this.updateBasic( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.ctx.fillStyle = ColorScheme.happy_fall_board_fill;
    this.ctx.fillRect( 0, 0, Game.screenW(), Game.screenH() );

    this.startRender();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  public name(): string
  {
    return "HappyFallScreen";
  }
}
